
import { useEffect } from 'react';

interface SocialMediaMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'video' | 'book';
  siteName?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

export const useSocialMediaMeta = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  siteName = 'Bobo & Teedee®',
  twitterCard = 'summary_large_image',
}: SocialMediaMetaProps) => {
  useEffect(() => {
    const currentUrl = url || window.location.href;
    const absoluteImageUrl = image ? (image.startsWith('http') ? image : `${window.location.origin}${image}`) : `${window.location.origin}/img/bobo-teedee-logo.png`;

    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, content: string) => {
      let metaTag = document.querySelector(selector);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        if (selector.includes('property=')) {
          metaTag.setAttribute('property', selector.split('"')[1]);
        } else if (selector.includes('name=')) {
          metaTag.setAttribute('name', selector.split('"')[1]);
        }
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Basic meta tags
    if (description) {
      updateMetaTag('meta[name="description"]', description);
    }
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', keywords);
    }

    // Open Graph tags
    if (title) {
      updateMetaTag('meta[property="og:title"]', title);
    }
    if (description) {
      updateMetaTag('meta[property="og:description"]', description);
    }
    updateMetaTag('meta[property="og:type"]', type);
    updateMetaTag('meta[property="og:url"]', currentUrl);
    updateMetaTag('meta[property="og:image"]', absoluteImageUrl);
    updateMetaTag('meta[property="og:site_name"]', siteName);

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', twitterCard);
    if (title) {
      updateMetaTag('meta[name="twitter:title"]', title);
    }
    if (description) {
      updateMetaTag('meta[name="twitter:description"]', description);
    }
    updateMetaTag('meta[name="twitter:image"]', absoluteImageUrl);

    // Additional Open Graph tags for better sharing
    updateMetaTag('meta[property="og:image:width"]', '1200');
    updateMetaTag('meta[property="og:image:height"]', '630');
    updateMetaTag('meta[property="og:image:alt"]', title || 'Bobo & Teedee Educational Adventures');

    // Facebook App ID (if needed in the future)
    // updateMetaTag('meta[property="fb:app_id"]', 'YOUR_FACEBOOK_APP_ID');

    // Additional Twitter tags
    updateMetaTag('meta[name="twitter:site"]', '@boboandteedee');
    updateMetaTag('meta[name="twitter:creator"]', '@boboandteedee');

  }, [title, description, keywords, image, url, type, siteName, twitterCard]);
};
